class ThemeAudit extends HTMLElement {
  constructor() {
    super()
    this.abortController = null
    this.progressInterval = null
    this.progressTimeout = null
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  connectedCallback() {
    this.form = this.querySelector('[data-theme-audit-form]')
    this.submitButton = this.querySelector('[data-theme-audit-submit]')
    this.submitLabel = this.submitButton?.querySelector('[data-theme-audit-submit-label]')
    this.status = this.querySelector('[data-theme-audit-status]')
    this.statusMessage = this.status?.querySelector('[data-theme-audit-status-message]')
    this.statusContact = this.status?.querySelector('[data-theme-audit-status-contact]')
    this.progress = this.querySelector('[data-theme-audit-progress]')
    this.progressFill = this.progress?.querySelector('.theme-audit-progress-fill')
    this.results = this.querySelector('[data-theme-audit-results]')
    this.translations = this.getTranslations()

    if (!this.form || !this.submitButton || !this.results) return

    this.form.addEventListener('submit', this.handleSubmit)
    this.applyQueryParameters()
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit)
    this.abortController?.abort()
    this.stopProgress()
  }

  getTranslations() {
    const element = this.querySelector('[data-theme-audit-translations]')

    try {
      return JSON.parse(element?.textContent || '{}')
    } catch {
      return {}
    }
  }

  applyQueryParameters() {
    const parameters = new URLSearchParams(window.location.search)
    const strategy = parameters.get('strategy') === 'desktop' ? 'desktop' : 'mobile'
    const strategyInput = this.form?.querySelector(`[name="strategy"][value="${strategy}"]`)
    const storeInput = this.form?.querySelector('[name="store"]')
    const store = parameters.get('url')?.trim()

    if (strategyInput) strategyInput.checked = true
    if (!storeInput || !store) return

    storeInput.value = store
    this.form.requestSubmit()
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  normalizeUrl(value) {
    const trimmedValue = value.trim()
    const valueWithProtocol = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmedValue)
      ? trimmedValue
      : `https://${trimmedValue}`
    const url = new URL(valueWithProtocol)

    if (!['http:', 'https:'].includes(url.protocol)) {
      throw new Error(this.translations.status.invalid_url)
    }

    if (!url.hostname.includes('.') && url.hostname !== 'localhost') {
      throw new Error(this.translations.status.invalid_url)
    }

    if (url.protocol === 'http:' && !['localhost', '127.0.0.1'].includes(url.hostname)) {
      url.protocol = 'https:'
    }

    url.hash = ''
    return url.href
  }

  setStatus(message, state = 'idle') {
    if (!this.status) return

    const isError = state === 'error' || state === 'error-contact'

    if (this.statusMessage) {
      this.statusMessage.textContent = message
    } else {
      this.status.textContent = message
    }

    if (this.statusContact) this.statusContact.hidden = state !== 'error-contact'
    this.status.hidden = !message
    this.status.classList.toggle('is-error', isError)
    this.status.classList.toggle('alert', isError)
    this.status.classList.toggle('alert-error', isError)
    this.status.setAttribute('role', isError ? 'alert' : 'status')
  }

  setProgress(value) {
    const progressValue = Math.min(Math.max(value, 0), 100)
    this.progress?.setAttribute('aria-valuenow', String(Math.round(progressValue)))
    this.progressFill?.style.setProperty('--score', `${progressValue}%`)
  }

  startProgress() {
    if (!this.progress || !this.progressFill) return

    this.stopProgress()
    const startedAt = Date.now()
    this.progress.hidden = false
    this.setProgress(6)
    this.progressInterval = window.setInterval(() => {
      const elapsedTime = Date.now() - startedAt
      this.setProgress(Math.min(95, 6 + (elapsedTime / 60000) * 89))
    }, 600)
  }

  stopProgress(complete = false) {
    window.clearInterval(this.progressInterval)
    window.clearTimeout(this.progressTimeout)

    if (!this.progress) return

    if (complete) {
      this.setProgress(100)
      this.progressTimeout = window.setTimeout(() => {
        this.progress.hidden = true
        this.setProgress(0)
      }, 600)
      return
    }

    this.progress.hidden = true
    this.setProgress(0)
  }

  getRatingClass(score, label) {
    const normalizedLabel = label?.toLowerCase() || ''

    if (normalizedLabel.includes('excellent')) return 'excellent'
    if (normalizedLabel.includes('good')) return 'good'
    if (normalizedLabel.includes('needs')) return 'needs-work'
    if (normalizedLabel.includes('poor') || typeof score !== 'number') return 'poor'
    if (score >= 85) return 'excellent'
    if (score >= 70) return 'good'
    if (score >= 50) return 'needs-work'
    return 'poor'
  }

  humanizeKey(key, translations = {}) {
    if (translations[key]) return translations[key]

    return key
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  renderScoreCard(label, score) {
    const ratingClass = this.getRatingClass(score)

    return `
      <article class="theme-audit-card">
        <span class="theme-audit-card-title">${this.escapeHtml(label)}</span>
        <strong class="theme-audit-score theme-audit-label-${ratingClass}">${this.escapeHtml(score ?? this.translations.results.not_available)}</strong>
      </article>
    `
  }

  renderThemeScore([key, value], descriptions = {}) {
    const ratingClass = this.getRatingClass(value.score, value.label)
    const score = typeof value.score === 'number' ? Math.min(Math.max(value.score, 0), 100) : 0
    const title = this.humanizeKey(key, this.translations.theme_scores)
    const label = value.label || this.translations.results.not_available
    const description = descriptions[key] || value.description

    return `
      <article class="theme-audit-card theme-audit-theme-card">
        <div class="theme-audit-theme-card-header">
          <span class="theme-audit-card-title theme-audit-theme-card-title">
            ${this.escapeHtml(title)}: <strong class="theme-audit-label-${ratingClass}">${this.escapeHtml(label)}</strong>
          </span>
          <strong class="theme-audit-score theme-audit-theme-score theme-audit-label-${ratingClass}">${this.escapeHtml(value.score ?? this.translations.results.not_available)}</strong>
        </div>
        <div
          class="theme-audit-progress theme-audit-progress-${ratingClass}"
          role="progressbar"
          aria-label="${this.escapeHtml(`${title} ${this.translations.results.score}`)}"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow="${this.escapeHtml(score)}"
        >
          <span class="theme-audit-progress-fill" style="--score: ${this.escapeHtml(score)}%;"></span>
        </div>
        ${description ? `<p>${this.escapeHtml(description)}</p>` : ''}
        ${value.recommendation ? `<p><strong>${this.escapeHtml(this.translations.results.recommendation)}:</strong> ${this.escapeHtml(value.recommendation)}</p>` : ''}
      </article>
    `
  }

  renderThemeInfo(theme) {
    const fields = ['name', 'schemaName', 'schemaVersion', 'role', 'id', 'themeStoreId', 'source']
      .map((key) => [this.translations.theme_fields[key], theme[key]])
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
    const statusClass = theme.detected ? 'success' : 'warning'
    const statusLabel = theme.detected
      ? this.translations.results.detected
      : this.translations.results.not_detected
    const statusMessage = theme.detected
      ? this.translations.results.detected_message
      : this.translations.results.not_detected_message
    let sourceUrl = ''

    try {
      const parsedSourceUrl = new URL(theme.sourceUrl)
      if (['http:', 'https:'].includes(parsedSourceUrl.protocol)) sourceUrl = parsedSourceUrl.href
    } catch {
      sourceUrl = ''
    }

    return `
      <section class="theme-audit-result-section theme-audit-theme-info">
        <div class="theme-audit-theme-info-header">
          <div>
            <h3>${this.escapeHtml(this.translations.results.detected_theme)}</h3>
            <p class="theme-audit-theme-info-status">${this.escapeHtml(statusMessage)}</p>
          </div>
          <span class="theme-audit-pill theme-audit-pill-${statusClass}">${this.escapeHtml(statusLabel)}</span>
        </div>
        ${fields.length ? `
          <dl class="theme-audit-detail-grid">
            ${fields.map(([label, value]) => `
              <div>
                <dt>${this.escapeHtml(label)}</dt>
                <dd>${this.escapeHtml(value)}</dd>
              </div>
            `).join('')}
          </dl>
        ` : ''}
        ${sourceUrl ? `<p class="theme-audit-theme-source">${this.escapeHtml(this.translations.results.source_url)}: <a href="${this.escapeHtml(sourceUrl)}" target="_blank" rel="noopener">${this.escapeHtml(sourceUrl)}</a></p>` : ''}
        ${theme.description ? `<p>${this.escapeHtml(theme.description)}</p>` : ''}
        ${theme.error ? `<p><strong>${this.escapeHtml(this.translations.results.detection_error)}:</strong> ${this.escapeHtml(theme.error)}</p>` : ''}
        ${theme.descriptionError ? `<p><strong>${this.escapeHtml(this.translations.results.description_error)}:</strong> ${this.escapeHtml(theme.descriptionError)}</p>` : ''}
      </section>
    `
  }

  renderList(items = [], ordered = false) {
    const tagName = ordered ? 'ol' : 'ul'
    return `<${tagName}>${items.map((item) => `<li>${this.escapeHtml(item)}</li>`).join('')}</${tagName}>`
  }

  renderRaw(value) {
    if (value === null || value === undefined) return ''

    if (Array.isArray(value) && value.every((item) => typeof item !== 'object' || item === null)) {
      return this.renderList(value)
    }

    if (typeof value === 'object') {
      return `<pre class="theme-audit-raw">${this.escapeHtml(JSON.stringify(value, null, 2))}</pre>`
    }

    return `<p>${this.escapeHtml(value)}</p>`
  }

  renderResults(response) {
    const data = response && typeof response === 'object' && 'data' in response ? response.data : response

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      this.results.innerHTML = `
        <h2>${this.escapeHtml(this.translations.results.audit_results)}</h2>
        <div class="theme-audit-result-section">${this.renderRaw(data)}</div>
      `
      this.results.hidden = false
      return
    }

    const theme = data.theme
    const pageSpeed = data.pageSpeed || {}
    const feedback = data.feedback || {}
    const scores = pageSpeed.scores || {}
    const metrics = pageSpeed.metrics || {}
    const themeScores = pageSpeed.themeScores || {}
    const categoryDescriptions = feedback.categoryDescriptions || {}
    const scoreKeys = ['mobilePerformance', 'accessibility', 'bestPractices', 'seo']
      .filter((key) => typeof scores[key] === 'number')
    const metricKeys = ['fcp', 'lcp', 'tbt', 'cls', 'speedIndex', 'interactive']
      .filter((key) => metrics[key])
    const findings = [...(pageSpeed.opportunities || []), ...(pageSpeed.diagnostics || [])]
      .filter((item) => ['high', 'low'].includes(String(item.impact)) && item.score !== 1)
      .slice(0, 8)
    const strategyLabel = pageSpeed.strategy === 'desktop'
      ? this.translations.form.desktop
      : this.translations.form.mobile

    this.results.innerHTML = `
      <header class="theme-audit-results-header">
        <p class="theme-audit-kicker">${this.escapeHtml(pageSpeed.strategy ? `${strategyLabel} ${this.translations.results.theme_audit}` : this.translations.results.theme_performance_check)}</p>
        <h2>${this.escapeHtml(this.translations.results.audit_results)}</h2>
        <p class="theme-audit-meta">${this.escapeHtml(pageSpeed.finalUrl || pageSpeed.url || '')}</p>
      </header>
      ${theme ? this.renderThemeInfo(theme) : ''}
      ${Object.keys(themeScores).length ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.theme_health)}</h3>
          <div class="theme-audit-theme-grid">
            ${Object.entries(themeScores).map((entry) => this.renderThemeScore(entry, categoryDescriptions)).join('')}
          </div>
        </section>
      ` : ''}
      ${scoreKeys.length ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.pagespeed_scores)}</h3>
          <div class="theme-audit-score-grid">
            ${scoreKeys.map((key) => this.renderScoreCard(this.humanizeKey(key, this.translations.scores), scores[key])).join('')}
          </div>
        </section>
      ` : ''}
      ${metricKeys.length ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.core_metrics)}</h3>
          <div class="theme-audit-metric-grid">
            ${metricKeys.map((key) => `
              <article class="theme-audit-card">
                <span class="theme-audit-card-title">${this.escapeHtml(this.humanizeKey(key, this.translations.metrics))}</span>
                <strong class="theme-audit-metric-value">${this.escapeHtml(metrics[key])}</strong>
              </article>
            `).join('')}
          </div>
        </section>
      ` : ''}
      ${feedback.topIssues?.length ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.top_issues)}</h3>
          ${this.renderList(feedback.topIssues)}
        </section>
      ` : ''}
      ${findings.length ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.technical_findings)}</h3>
          ${this.renderList(findings.map((item) => `${item.title || item.id}${item.displayValue ? `: ${item.displayValue}` : ''}`))}
        </section>
      ` : ''}
      ${feedback.summary ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.theme_summary)}</h3>
          <p>${this.escapeHtml(feedback.summary)}</p>
        </section>
      ` : ''}
      ${feedback.switchThemeRecommendation ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.switch_recommendation)}</h3>
          <p>${this.escapeHtml(feedback.switchThemeRecommendation)}</p>
        </section>
      ` : ''}
      ${feedback.nextSteps?.length ? `
        <section class="theme-audit-result-section">
          <h3>${this.escapeHtml(this.translations.results.next_steps)}</h3>
          ${this.renderList(feedback.nextSteps, true)}
        </section>
      ` : ''}
    `
    this.results.hidden = false
  }

  async handleSubmit(event) {
    event.preventDefault()
    const formData = new FormData(this.form)
    const storeValue = String(formData.get('store') || '').trim()
    const strategy = formData.get('strategy') === 'desktop' ? 'desktop' : 'mobile'

    if (!storeValue) {
      this.setStatus(this.translations.status.enter_url, 'error')
      return
    }

    let storeUrl

    try {
      storeUrl = this.normalizeUrl(storeValue)
    } catch (error) {
      this.setStatus(error instanceof Error ? error.message : this.translations.status.invalid_url, 'error')
      return
    }

    const storeInput = this.form.querySelector('[name="store"]')
    if (storeInput) storeInput.value = storeUrl

    this.abortController?.abort()
    this.abortController = new AbortController()
    const timeout = window.setTimeout(() => this.abortController.abort(), 90000)
    const endpoint = new URL(this.dataset.endpoint)
    endpoint.searchParams.set('url', storeUrl)
    endpoint.searchParams.set('strategy', strategy)

    this.submitButton.disabled = true
    if (this.submitLabel) this.submitLabel.textContent = this.translations.form.submitting
    this.results.hidden = true
    this.results.innerHTML = ''
    this.setStatus(this.translations.status.auditing)
    this.startProgress()

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { Accept: 'application/json, text/plain;q=0.9' },
        signal: this.abortController.signal,
      })
      const isJson = (response.headers.get('content-type') || '').includes('application/json')
      const payload = isJson ? await response.json() : await response.text()

      if (!response.ok) {
        const message = typeof payload === 'string'
          ? payload
          : payload?.error || payload?.message || `${this.translations.status.failed} (${response.status})`
        throw new Error(message)
      }

      this.renderResults(payload)
      this.setStatus(this.translations.status.complete)
      this.stopProgress(true)
    } catch (error) {
      if (!this.isConnected) return

      const isTimeout = error instanceof Error && error.name === 'AbortError'
      const message = isTimeout
        ? this.translations.status.timeout
        : this.translations.status.failed
      this.setStatus(message, 'error-contact')
      this.stopProgress()
    } finally {
      window.clearTimeout(timeout)
      this.submitButton.disabled = false
      if (this.submitLabel) this.submitLabel.textContent = this.translations.form.submit
    }
  }
}

if (!customElements.get('theme-audit')) {
  customElements.define('theme-audit', ThemeAudit)
}
